import { SERVER_URL } from '../DB_Url';
import { getAuthToken, clearAuthToken } from '../hooks/useAuthToken';
import { ApiError } from './apiErrors';

const DEFAULT_TIMEOUT_MS = 20_000;
const MAX_RETRIES = 2;

function sleep(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }

function getRetryDelay(attempt, response) {
  if (response?.headers?.has('Retry-After')) {
    const seconds = parseInt(response.headers.get('Retry-After'), 10);
    if (!isNaN(seconds)) return seconds * 1000;
  }
  return Math.min(1000 * Math.pow(2, attempt), 10_000);
}

function isRetryable(status) {
  return status >= 500 || status === 408 || status === 429;
}

// snake_case ↔ camelCase conversion (handles nested objects + arrays)
function snakeToCamel(str) {
  return str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
}

function camelToSnake(str) {
  return str.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
}

function convertKeys(obj, converter) {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(item => convertKeys(item, converter));
  if (typeof obj !== 'object' || obj instanceof Date) return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[converter(key)] = convertKeys(value, converter);
  }
  return result;
}

function convertKeysToCamelCase(obj) {
  return convertKeys(obj, snakeToCamel);
}

function convertKeysToSnakeCase(obj) {
  return convertKeys(obj, camelToSnake);
}

async function safeJsonBody(response) {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch { return null; }
}

export async function apiRequest(endpoint, options = {}) {
  const {
    method = 'GET',
    body,
    headers: extraHeaders,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries = MAX_RETRIES,
    skipAuth = false,
    skipCamelCase = false,
    baseUrl,
  } = options;

  const url = `${baseUrl || SERVER_URL}/api/inventory${endpoint}`;

  const headers = { 'Content-Type': 'application/json', ...extraHeaders };

  if (!skipAuth) {
    const token = await getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const requestBody = body ? JSON.stringify(skipCamelCase ? body : convertKeysToSnakeCase(body)) : undefined;
      const response = await fetch(url, {
        method,
        headers,
        body: requestBody,
        signal: controller.signal,
      });
      clearTimeout(timeout);

      // 401 → refresh token once, retry
      if (response.status === 401 && !skipAuth && attempt === 0) {
        clearAuthToken();
        const newToken = await getAuthToken(true);
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          continue; // retry with new token
        }
      }

      if (!response.ok) {
        const errorBody = await safeJsonBody(response);
        const err = new ApiError(response.status, errorBody);
        if (isRetryable(response.status) && attempt < retries && method === 'GET') {
          lastError = err;
          await sleep(getRetryDelay(attempt, response));
          continue;
        }
        throw err;
      }

      if (response.status === 204) return null;

      const raw = await safeJsonBody(response);
      return skipCamelCase ? raw : convertKeysToCamelCase(raw);
    } catch (error) {
      clearTimeout(timeout);
      if (error instanceof ApiError) throw error;
      if (error.name === 'AbortError') throw new Error(`Request timed out after ${timeoutMs}ms`);
      if (attempt < retries && method === 'GET') {
        lastError = error;
        await sleep(getRetryDelay(attempt));
        continue;
      }
      throw error;
    }
  }
  throw lastError || new Error('Unknown fetch error');
}
